/**
 * Hero SVG Morphing Waves Animation
 * Creates layered wave paths with smooth morphing
 */

class WaveAnimation {
  constructor(svg) {
    this.svg = svg;
    this.waves = [];
    this.animationId = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.width = 1440;
    this.height = 900;

    // Wave configs - varied amplitudes, horizontal drift for organic motion
    this.waveConfigs = [
      { id: 'wave-1', baseY: 120, amplitude: 100, frequency: 1.5, speed: 0.0012, phase: 0, drift: 0.0003 },
      { id: 'wave-2', baseY: 280, amplitude: 60, frequency: 2.2, speed: 0.0010, phase: Math.PI / 4, drift: -0.0004 },
      { id: 'wave-3', baseY: 460, amplitude: 80, frequency: 1.2, speed: 0.0008, phase: Math.PI / 2, drift: 0.0002 },
      { id: 'wave-4', baseY: 640, amplitude: 50, frequency: 1.8, speed: 0.0011, phase: Math.PI * 0.75, drift: -0.0003 },
      { id: 'wave-5', baseY: 800, amplitude: 70, frequency: 1.6, speed: 0.0009, phase: Math.PI * 0.3, drift: 0.0005 },
    ];

    this.init();
  }

  init() {
    this.waveConfigs.forEach((config) => {
      const path = document.getElementById(config.id);
      if (path) {
        this.waves.push({ path, config });
      }
    });

    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      if (this.reducedMotion && this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      } else if (!this.reducedMotion && !this.animationId) {
        this.animate();
      }
    });

    this.draw(0);

    if (!this.reducedMotion) {
      this.animate();
    }
  }

  generateWavePath(config, time) {
    const { baseY, amplitude, frequency, speed, phase, drift } = config;
    const points = [];
    const segments = 12;

    // Horizontal drift offset
    const xDrift = Math.sin(time * drift) * 80;

    // Start from bottom-left corner
    points.push(`M 0 ${this.height}`);

    // Generate wave points across the width
    for (let i = 0; i <= segments; i++) {
      const baseX = (i / segments) * this.width;
      const x = baseX + xDrift * Math.sin((i / segments) * Math.PI);
      const waveOffset = time * speed;

      // Combine multiple sine waves for organic motion
      const y = baseY +
        Math.sin((baseX / this.width) * Math.PI * frequency + waveOffset + phase) * amplitude +
        Math.sin((baseX / this.width) * Math.PI * frequency * 0.5 + waveOffset * 0.7 + phase * 1.3) * (amplitude * 0.4) +
        Math.sin((baseX / this.width) * Math.PI * frequency * 2 + waveOffset * 1.5 + phase * 0.6) * (amplitude * 0.2);

      if (i === 0) {
        points.push(`L 0 ${y}`);
      }
      points.push(`${x} ${y}`);
    }

    // Close the path to bottom-right corner
    points.push(`L ${this.width} ${this.height}`);
    points.push('Z');

    // Convert points to smooth curve using quadratic bezier
    return this.pointsToSmoothPath(points);
  }

  pointsToSmoothPath(points) {
    // For this wave effect, we'll use the simple path
    // First two elements are M and L commands
    let path = points[0] + ' ' + points[1] + ' ';

    // Build smooth curve through intermediate points
    const curvePoints = points.slice(2, -2);
    if (curvePoints.length >= 2) {
      path += 'Q ';
      for (let i = 0; i < curvePoints.length - 1; i++) {
        const current = curvePoints[i].split(' ').map(Number);
        const next = curvePoints[i + 1].split(' ').map(Number);
        const midX = (current[0] + next[0]) / 2;
        const midY = (current[1] + next[1]) / 2;

        if (i === 0) {
          path += `${current[0]} ${current[1]} ${midX} ${midY} `;
        } else {
          path += `T ${midX} ${midY} `;
        }
      }

      // Final point before closing
      const last = curvePoints[curvePoints.length - 1];
      path += `T ${last} `;
    }

    // Add closing commands
    path += points[points.length - 2] + ' ' + points[points.length - 1];

    return path;
  }

  draw(time) {
    this.waves.forEach(({ path, config }) => {
      const d = this.generateWavePath(config, time);
      path.setAttribute('d', d);
    });
  }

  animate() {
    const tick = (timestamp) => {
      this.draw(timestamp);
      this.animationId = requestAnimationFrame(tick);
    };

    this.animationId = requestAnimationFrame(tick);
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const svg = document.getElementById('hero-waves');
  if (svg) {
    new WaveAnimation(svg);
  }
});
